import { useEffect, useState, useMemo, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/utils/Loading";
import DeleteModal from "../../../components/modals/DeleteModal";
import {
  handleAddFormSubmit,
  handleEditFormSubmit,
} from "../../../components/modals/EditableTable";
import { formattedError } from "../../../components/formatted/FormattedText";
import WasteFlowGeneralTab from "./tabs/WasteFlowGeneralTab";
import WasteFlowCostsTab from "./tabs/WasteFlowCostsTab";
import WasteFlowSortingTab from "./tabs/WasteFlowSortingTab";
import SettingsCreateWizardLayout from "../SettingsCreateWizardLayout";
import SettingsWizardFooter from "../SettingsWizardFooter";
import SettingsChangeHistoryPanel from "../SettingsChangeHistoryPanel";
import {
  DEFAULT_WASTE_FORM,
  DEFAULT_WASTE_CREATE_FORM,
  TAB_IDS,
  WASTE_FLOW_LAYOUT_WIDTH_PX,
} from "./wasteFlowConstants";
import {
  buildSortingItemsFromAuthorized,
  fetchMaterialsCatalog,
  hasActiveSortingMaterialsForStream,
  mergeSortingItemsWithCatalog,
} from "./wasteSortingItems";
import {
  getWizardStepIds,
  getWizardStepIndex,
  WASTE_WIZARD_STEP_IDS,
} from "./wasteFlowWizard";
import {
  buildWasteFlowCreateDataFromTemplate,
  getWasteFlowTemplateById,
} from "./wasteFlowTemplates";
import trashIcon from "../../../static/assets/Trash.svg";

const ensureWasteShape = (raw) => ({
  ...DEFAULT_WASTE_FORM,
  ...raw,
  density: raw?.density ?? "",
  treatmentCostUnit: raw?.treatmentCostUnit || "ton",
  authorizedMaterial: Array.isArray(raw?.authorizedMaterial)
    ? raw.authorizedMaterial
    : [],
});

const buildWastePayloadFromData = (formData) => {
  const {
    _id,
    __v,
    createdAt,
    updatedAt,
    corporation,
    labelWithoutWhiteSpaces,
    message,
    ...fields
  } = formData || {};

  return {
    ...fields,
    density: formData?.density === "" ? 0 : Number(formData?.density),
    treatmentCostFull:
      formData?.treatmentCostFull === "" ||
      formData?.treatmentCostFull === null ||
      formData?.treatmentCostFull === undefined
        ? 0
        : Number(formData?.treatmentCostFull),
    treatmentCostUnit: formData?.treatmentCostUnit || "ton",
    displayColor: formData?.displayColor || DEFAULT_WASTE_FORM.displayColor,
    hazardous: Boolean(formData?.hazardous),
    isValorizationEligible: Boolean(formData?.isValorizationEligible),
    active: formData?.active !== false,
  };
};

const SettingsWasteEditor = ({ token, API, mode, basePath }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isCreate = mode === "create";

  const [formData, setFormData] = useState(
    isCreate ? { ...DEFAULT_WASTE_CREATE_FORM } : { ...DEFAULT_WASTE_FORM },
  );
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [sortingItems, setSortingItems] = useState([]);
  const [materialsCatalogReady, setMaterialsCatalogReady] = useState(false);
  const [initialEditPayload, setInitialEditPayload] = useState(null);
  const actionsRef = useRef(null);

  const showHwSortingStep = useMemo(
    () =>
      hasActiveSortingMaterialsForStream(
        sortingItems,
        "hw",
        materialsCatalogReady,
      ),
    [sortingItems, materialsCatalogReady],
  );

  const wizardStepIds = useMemo(
    () => getWizardStepIds({ includeHw: showHwSortingStep }),
    [showHwSortingStep],
  );

  const wizardSteps = useMemo(() => {
    const steps = [
      { id: TAB_IDS.general, label: t("General information") },
      { id: TAB_IDS.costs, label: t("Treatment cost") },
      {
        id: TAB_IDS.proUnauthorized,
        label: t("settingsWaste.tabProUnauthorized"),
      },
    ];
    if (showHwSortingStep) {
      steps.push({
        id: TAB_IDS.hwUnauthorized,
        label: t("settingsWaste.tabHwUnauthorized"),
      });
    }
    return steps;
  }, [t, showHwSortingStep]);

  const initialTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    const allowed = isCreate
      ? WASTE_WIZARD_STEP_IDS
      : [...WASTE_WIZARD_STEP_IDS, TAB_IDS.modifications];
    return tab && allowed.includes(tab) ? tab : TAB_IDS.general;
  }, [location.search, isCreate]);
  const templateId = useMemo(
    () => new URLSearchParams(location.search).get("template"),
    [location.search],
  );

  const [activeTab, setActiveTab] = useState(initialTab);
  const [maxReachedWizardStepIndex, setMaxReachedWizardStepIndex] = useState(0);

  const createStepIndex = useMemo(
    () => (isCreate ? getWizardStepIndex(activeTab, wizardStepIds) : 0),
    [isCreate, activeTab, wizardStepIds],
  );

  const isLastWizardStep = createStepIndex === wizardSteps.length - 1;

  useEffect(() => {
    if (!isCreate) setActiveTab(initialTab);
  }, [initialTab, isCreate]);

  const setWizardStep = (stepId) => {
    setActiveTab(stepId);
    const params = new URLSearchParams(location.search);
    if (stepId === TAB_IDS.general) {
      params.delete("tab");
    } else {
      params.set("tab", stepId);
    }
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
    });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    if (tabId === TAB_IDS.general) {
      params.delete("tab");
    } else {
      params.set("tab", tabId);
    }
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
    });
  };

  useEffect(
    () => {
      if (
        materialsCatalogReady &&
        !showHwSortingStep &&
        activeTab === TAB_IDS.hwUnauthorized
      ) {
        if (isCreate) {
          setWizardStep(TAB_IDS.proUnauthorized);
        } else {
          handleTabChange(TAB_IDS.proUnauthorized);
        }
      }
    },
    // eslint-disable-next-line
    [materialsCatalogReady, showHwSortingStep, activeTab, isCreate],
  );

  const tabs = useMemo(() => {
    const list = [
      { id: TAB_IDS.general, label: t("General information") },
      { id: TAB_IDS.costs, label: t("Treatment cost") },
      {
        id: TAB_IDS.proUnauthorized,
        label: t("settingsWaste.tabProUnauthorized"),
      },
    ];
    if (showHwSortingStep) {
      list.push({
        id: TAB_IDS.hwUnauthorized,
        label: t("settingsWaste.tabHwUnauthorized"),
      });
    }
    list.push({
      id: TAB_IDS.modifications,
      label: t("settingsHistory.tab"),
    });
    return list;
  }, [t, showHwSortingStep]);

  useEffect(() => {
    if (!isCreate) return undefined;
    if (!token) {
      setMaterialsCatalogReady(false);
      return undefined;
    }

    let cancelled = false;

    const loadCreateEditor = async () => {
      setIsLoading(true);
      setLoadError(null);
      setMaterialsCatalogReady(false);
      setInitialEditPayload(null);

      try {
        await fetchMaterialsCatalog(API, token);
        if (cancelled) return;
        setMaterialsCatalogReady(true);

        const template = getWasteFlowTemplateById(templateId, t);
        const initialData = buildWasteFlowCreateDataFromTemplate(template);
        setFormData(initialData);
        setSortingItems(
          buildSortingItemsFromAuthorized(initialData.authorizedMaterial),
        );
      } catch {
        if (!cancelled) setLoadError("error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadCreateEditor();
    return () => {
      cancelled = true;
    };
  }, [API, token, isCreate, templateId, t]);

  useEffect(() => {
    if (isCreate) return undefined;
    if (!token || !id) return undefined;

    let cancelled = false;

    const loadEditEditor = async () => {
      setIsLoading(true);
      setLoadError(null);
      setMaterialsCatalogReady(false);

      try {
        await fetchMaterialsCatalog(API, token);
        if (cancelled) return;
        setMaterialsCatalogReady(true);

        const response = await axios.get(
          `${API}wastecategory?page=1&limit=500`,
          {
            headers: { authorization: `Bearer ${token}` },
            params: {
              keywordFilter: "",
              listUnCodeFullFilter: {},
              listUnCodeEmptyFilter: {},
              listWasteCodeFullFilter: {},
              listWasteCodeEmptyFilter: {},
              sortByField: "label",
              orderSortByField: "1",
            },
          },
        );
        if (cancelled) return;

        const found =
          (response.data?.data || []).find((w) => w._id === id) || null;
        if (!found) {
          setLoadError("not_found");
          setInitialEditPayload(null);
        } else {
          const shaped = ensureWasteShape(found);
          setFormData(shaped);
          setSortingItems((prev) =>
            mergeSortingItemsWithCatalog(shaped.authorizedMaterial, prev),
          );
          setInitialEditPayload(buildWastePayloadFromData(shaped));
        }
      } catch {
        if (!cancelled) {
          setLoadError("error");
          setInitialEditPayload(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadEditEditor();
    return () => {
      cancelled = true;
    };
  }, [API, token, id, isCreate]);

  useEffect(() => {
    if (!actionsOpen) return;
    const close = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [actionsOpen]);

  const pageTitle = isCreate
    ? t("settingsWaste.createNewFlow")
    : formData?.label || t("Waste Category");

  const validateWizardStep = (stepIndex) => {
    if (stepIndex === 0) {
      if (!String(formData.label || "").trim()) {
        toast.error(t("settingsWaste.nameRequired"), {
          className: "custom-toast",
        });
        return false;
      }
      if (
        formData.density === "" ||
        formData.density === null ||
        formData.density === undefined
      ) {
        toast.error(t("settingsWaste.densityRequired"), {
          className: "custom-toast",
        });
        return false;
      }
      if (!String(formData.displayColor || "").trim()) {
        toast.error(t("settingsWaste.colorRequired"), {
          className: "custom-toast",
        });
        return false;
      }
      return true;
    }
    return true;
  };

  const validateBeforeSave = () => {
    if (!validateWizardStep(0)) return false;
    if (!validateWizardStep(1)) return false;
    return true;
  };

  const currentPayload = useMemo(
    () => buildWastePayloadFromData(formData),
    [formData],
  );

  const hasEditChanges = useMemo(() => {
    if (isCreate) return false;
    if (!initialEditPayload) return false;
    return (
      JSON.stringify(currentPayload) !== JSON.stringify(initialEditPayload)
    );
  }, [isCreate, initialEditPayload, currentPayload]);

  const persistWaste = async () => {
    const payload = currentPayload;

    if (formData._id) {
      return handleEditFormSubmit(
        { ...payload, _id: formData._id },
        "wastecategory",
      );
    }

    const response = await handleAddFormSubmit(
      { preventDefault: () => {} },
      payload,
      setFormData,
      DEFAULT_WASTE_FORM,
      [],
      () => {},
      0,
      () => {},
      "wastecategory",
      null,
      setSaving,
    );

    if (response?.status === 200 && response.data?._id) {
      setFormData((prev) => ({ ...prev, _id: response.data._id }));
    }

    return response;
  };

  const saveWaste = async () => {
    if (!validateBeforeSave()) return;

    setSaving(true);

    try {
      const response = await persistWaste();
      if (response?.status === 200) {
        toast.success(
          isCreate
            ? t("The flux has been successfully created")
            : t("material_updated"),
          { className: "custom-toast" },
        );
        history.push(basePath);
        return;
      } else if (response) {
        toast.error(
          formattedError(response?.data?.error) || t("An error occurred"),
          { className: "custom-toast" },
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleQuitAndSave = async () => {
    if (!validateBeforeSave()) return;
    setSaving(true);
    try {
      const response = await persistWaste();
      if (response?.status === 200) {
        toast.success(
          isCreate
            ? t("The flux has been successfully created")
            : t("material_updated"),
          { className: "custom-toast" },
        );
        history.push(basePath);
      } else if (response) {
        toast.error(
          formattedError(response?.data?.error) || t("An error occurred"),
          { className: "custom-toast" },
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const goToWizardStepClick = (_index, step) => {
    if (step?.id) setWizardStep(step.id);
  };

  const handleWizardQuit = () => {
    if (createStepIndex === 0) {
      history.push(basePath);
      return;
    }
    handleQuitAndSave();
  };

  const handleWizardContinue = () => {
    if (!validateWizardStep(createStepIndex)) return;
    if (isLastWizardStep) {
      saveWaste();
      return;
    }
    setWizardStep(wizardStepIds[createStepIndex + 1]);
    setMaxReachedWizardStepIndex((prev) => Math.max(prev, createStepIndex + 1));
  };

  const actionsMenuItems =
    !isCreate && formData?._id
      ? [
          {
            label: t("settingsWaste.deleteFlow"),
            icon: (
              <img
                src={trashIcon}
                alt=""
                width={16}
                height={16}
                className="waste-flow-actions-menu__icon-img"
              />
            ),
            onClick: () => setDeleteModal(true),
          },
        ]
      : [];

  const actionsButton =
    actionsMenuItems.length > 0 ? (
      <div className="relative" ref={actionsRef}>
        <button
          type="button"
          onClick={() => setActionsOpen((o) => !o)}
          className="flex items-center gap-2 h-10 px-4 rounded-full border border-[#d3d3d3] bg-white hover:bg-[#f8f8f8] transition-colors text-sm font-semibold text-[#222222] cursor-pointer"
          aria-expanded={actionsOpen}
          aria-haspopup="menu"
        >
          <span>{t("Actions")}</span>
          <i className="fas fa-chevron-down text-xs" aria-hidden />
        </button>
        {actionsOpen && (
          <div
            className="waste-flow-actions-menu"
            role="menu"
            aria-label={t("Actions")}
          >
            {actionsMenuItems.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                className="waste-flow-actions-menu__item"
                onClick={() => {
                  item.onClick?.();
                  setActionsOpen(false);
                }}
              >
                {item.icon && (
                  <span className="waste-flow-actions-menu__icon" aria-hidden>
                    {item.icon}
                  </span>
                )}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    ) : null;

  const renderTabContent = () => {
    switch (activeTab) {
      case TAB_IDS.general:
        return (
          <WasteFlowGeneralTab formData={formData} setFormData={setFormData} />
        );
      case TAB_IDS.costs:
        return (
          <WasteFlowCostsTab formData={formData} setFormData={setFormData} />
        );
      case TAB_IDS.proUnauthorized:
        return (
          <WasteFlowSortingTab
            formData={formData}
            setFormData={setFormData}
            items={sortingItems}
            setItems={setSortingItems}
            streamType="pro"
            catalogReady={materialsCatalogReady}
          />
        );
      case TAB_IDS.hwUnauthorized:
        return (
          <WasteFlowSortingTab
            formData={formData}
            setFormData={setFormData}
            items={sortingItems}
            setItems={setSortingItems}
            streamType="hw"
            catalogReady={materialsCatalogReady}
          />
        );
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (isCreate) {
      return (
        <SettingsWizardFooter
          stepIndex={createStepIndex}
          saving={saving}
          onBack={() => setWizardStep(wizardStepIds[createStepIndex - 1])}
          onQuit={handleWizardQuit}
          quitLabel={
            createStepIndex > 0
              ? t("settingsWaste.quitAndSave")
              : t("settingsWaste.quit")
          }
          primaryLabel={
            isLastWizardStep ? t("settingsWaste.createFlow") : t("Continue")
          }
          onPrimary={handleWizardContinue}
        />
      );
    }

    if (!isCreate && activeTab === TAB_IDS.modifications) return null;

    if (!hasEditChanges) return null;

    return (
      <div className="settings-waste-editor__footer settings-waste-editor__footer--pinned">
        <div
          className="mx-auto w-full py-[22px] px-8 flex justify-end items-center gap-2"
          style={{ maxWidth: WASTE_FLOW_LAYOUT_WIDTH_PX }}
        >
          <button
            type="button"
            className="modal-button modal-button-cancel"
            onClick={() => history.push(basePath)}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`modal-button modal-button-save ${
              saving ? "disabled" : ""
            }`}
            disabled={saving}
            onClick={() => saveWaste()}
          >
            {t("Save")}
          </button>
        </div>
      </div>
    );
  };

  if (!token) return null;

  if (isLoading) {
    return (
      <div className="settings-page-wrapper">
        <Loading color="#F5F5F5" />
      </div>
    );
  }

  if (!isCreate && loadError) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader
          title={t("Waste Category")}
          showBackButton
          backRoute={basePath}
          backLabel={t("settingsWaste.backToFlows")}
        />
        <div className="settings-content-wrapper flex flex-col justify-center items-center py-16 gap-4">
          <p className="text-[#222] text-center max-w-md m-0">
            {loadError === "not_found"
              ? t("settingsWaste.flowNotFound")
              : t("settingsWaste.loadFlowError")}
          </p>
          <button
            type="button"
            className="modal-button modal-button-save"
            onClick={() => history.push(basePath)}
          >
            {t("settingsWaste.backToFlows")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`settings-page-wrapper settings-waste-editor${
        isCreate
          ? " settings-page-wrapper--wizard settings-waste-editor--create"
          : ""
      }`}
    >
      <PageHeader
        title={pageTitle}
        showBackButton
        backRoute={basePath}
        backLabel={
          isCreate ? t("settingsWaste.quit") : t("settingsWaste.backToFlows")
        }
        tabs={isCreate ? [] : tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        actions={actionsButton}
        actionsMenuItems={actionsMenuItems}
      />
      {isCreate ? (
        <SettingsCreateWizardLayout
          steps={wizardSteps}
          currentIndex={createStepIndex}
          maxReachedIndex={maxReachedWizardStepIndex}
          onStepClick={goToWizardStepClick}
        >
          {renderTabContent()}
        </SettingsCreateWizardLayout>
      ) : activeTab === TAB_IDS.modifications ? (
        <div className="settings-content-wrapper">
          <SettingsChangeHistoryPanel entityType="waste" entityId={id} />
        </div>
      ) : (
        <div className="settings-content-wrapper settings-waste-editor__content">
          <div
            className="mx-auto w-full flex justify-center px-[35px] pb-6 box-border"
            style={{ maxWidth: WASTE_FLOW_LAYOUT_WIDTH_PX }}
          >
            {renderTabContent()}
          </div>
        </div>
      )}

      {renderFooter()}

      {deleteModal && formData?._id && (
        <DeleteModal
          tableData={[]}
          setTableData={() => {}}
          modal={deleteModal}
          setModal={setDeleteModal}
          id={formData._id}
          message1={`${t("Do you really want to delete the material")} : \n${formData.label} ?`}
          message2={formData.label}
          messageValidation={t("The flux has been successfully deleted")}
          setIsDeleted={() => history.push(basePath)}
          path="wastecategory/delete/"
        />
      )}
    </div>
  );
};

export default SettingsWasteEditor;
